import { useState } from 'react';

interface ComparisonResult {
  pass1: { id: string; utterances: number; words: number; speakers: string[]; avgUtteranceDuration: number };
  pass2: { id: string; utterances: number; words: number; language: string; speakers: string[]; avgUtteranceDuration: number };
  comparison: {
    current: { strategy: string; words: number; lines: number; duplicates: number; speakers: string[]; qualityOk: boolean };
    u2Primary: { strategy: string; words: number; lines: number; duplicates: number; speakers: string[]; stats: Record<string, number> & { detectedIntros?: Array<{ name: string; role: string; speaker: string }> } };
  };
  transcripts: { current: string; u2Primary: string };
}

export default function TestMergeU2() {
  const [sessionId, setSessionId] = useState('');
  const [pass1Id, setPass1Id] = useState('');
  const [pass2Id, setPass2Id] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ComparisonResult | null>(null);
  const [activeTab, setActiveTab] = useState<'current' | 'u2'>('u2');

  const runComparison = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const body: Record<string, string> = {};
      if (sessionId) body.sessionId = sessionId;
      if (pass1Id) body.pass1Id = pass1Id;
      if (pass2Id) body.pass2Id = pass2Id;

      const response = await fetch('/api/test-merge-u2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Request failed');

      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-navy">Merge Strategy Comparison</h1>
        <p className="text-steel mt-1">Compare P1-primary (current) vs U2-primary (experimental) merge</p>
      </div>

      {/* Input Form */}
      <div className="bg-white border border-steel/20 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Session ID</label>
            <input
              type="text"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              placeholder="6fa8b3d3-afcc-..."
              className="w-full px-3 py-2 border border-steel/30 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Pass 1 ID (optional)</label>
            <input
              type="text"
              value={pass1Id}
              onChange={(e) => setPass1Id(e.target.value)}
              placeholder="AssemblyAI transcript ID"
              className="w-full px-3 py-2 border border-steel/30 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Pass 2 ID (optional)</label>
            <input
              type="text"
              value={pass2Id}
              onChange={(e) => setPass2Id(e.target.value)}
              placeholder="AssemblyAI transcript ID"
              className="w-full px-3 py-2 border border-steel/30 rounded-md text-sm"
            />
          </div>
        </div>
        <button
          onClick={runComparison}
          disabled={loading || (!sessionId && (!pass1Id || !pass2Id))}
          className="px-4 py-2 bg-[#1F4FFF] text-white rounded-md text-sm font-medium hover:bg-[#1a43d9] disabled:opacity-50"
        >
          {loading ? 'Running comparison...' : 'Compare Merge Strategies'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>
      )}

      {result && (
        <>
          {/* Raw Pass Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-steel/20 rounded-lg p-4">
              <h3 className="font-semibold text-navy mb-2">Pass 1 (U3 Pro, English-locked)</h3>
              <div className="text-sm text-steel space-y-1">
                <p>Utterances: <span className="font-mono text-navy">{result.pass1.utterances}</span></p>
                <p>Words: <span className="font-mono text-navy">{result.pass1.words}</span></p>
                <p>Avg utterance: <span className="font-mono text-navy">{(result.pass1.avgUtteranceDuration / 1000).toFixed(1)}s</span></p>
                <p>Speakers: <span className="font-mono text-navy">{result.pass1.speakers.join(', ')}</span></p>
              </div>
            </div>
            <div className="bg-white border border-steel/20 rounded-lg p-4">
              <h3 className="font-semibold text-navy mb-2">Pass 2 (U2, {result.pass2.language})</h3>
              <div className="text-sm text-steel space-y-1">
                <p>Utterances: <span className="font-mono text-navy">{result.pass2.utterances}</span></p>
                <p>Words: <span className="font-mono text-navy">{result.pass2.words}</span></p>
                <p>Avg utterance: <span className="font-mono text-navy">{(result.pass2.avgUtteranceDuration / 1000).toFixed(1)}s</span></p>
                <p>Speakers: <span className="font-mono text-navy">{result.pass2.speakers.join(', ')}</span></p>
              </div>
            </div>
          </div>

          {/* Comparison Stats */}
          <div className="bg-white border border-steel/20 rounded-lg p-4">
            <h3 className="font-semibold text-navy mb-3">Merge Comparison</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-steel/10">
                  <th className="text-left py-2 text-steel font-medium">Metric</th>
                  <th className="text-center py-2 text-steel font-medium">P1-Primary (current)</th>
                  <th className="text-center py-2 text-steel font-medium">U2-Primary (new)</th>
                  <th className="text-center py-2 text-steel font-medium">Winner</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Words', c: result.comparison.current.words, u: result.comparison.u2Primary.words, lowerBetter: false },
                  { label: 'Lines', c: result.comparison.current.lines, u: result.comparison.u2Primary.lines, lowerBetter: false },
                  { label: 'Duplicates', c: result.comparison.current.duplicates, u: result.comparison.u2Primary.duplicates, lowerBetter: true },
                  { label: 'Speakers', c: result.comparison.current.speakers.length, u: result.comparison.u2Primary.speakers.length, lowerBetter: false },
                ].map((row) => {
                  const winner = row.lowerBetter
                    ? (row.u < row.c ? 'u2' : row.c < row.u ? 'current' : 'tie')
                    : (row.u > row.c ? 'u2' : row.c > row.u ? 'current' : 'tie');
                  return (
                    <tr key={row.label} className="border-b border-steel/5">
                      <td className="py-2 text-navy">{row.label}</td>
                      <td className={`py-2 text-center font-mono ${winner === 'current' ? 'text-green-600 font-bold' : 'text-steel'}`}>{row.c}</td>
                      <td className={`py-2 text-center font-mono ${winner === 'u2' ? 'text-green-600 font-bold' : 'text-steel'}`}>{row.u}</td>
                      <td className="py-2 text-center text-xs">{winner === 'tie' ? '=' : winner === 'u2' ? 'U2' : 'P1'}</td>
                    </tr>
                  );
                })}
                <tr className="border-b border-steel/5">
                  <td className="py-2 text-navy">Speakers</td>
                  <td className="py-2 text-center text-xs text-steel">{result.comparison.current.speakers.join(', ')}</td>
                  <td className="py-2 text-center text-xs text-steel">{result.comparison.u2Primary.speakers.join(', ')}</td>
                  <td />
                </tr>
              </tbody>
            </table>
            {result.comparison.u2Primary.stats && (
              <div className="mt-3 text-xs text-steel">
                <span className="font-medium">U2 merge details:</span>{' '}
                Intros detected: {result.comparison.u2Primary.stats.introductionsDetected},{' '}
                Utterances split: {result.comparison.u2Primary.stats.utterancesSplit},{' '}
                Speakers relabeled: {result.comparison.u2Primary.stats.speakersRelabeled},{' '}
                Voice alerts: {result.comparison.u2Primary.stats.voiceAlertsInjected},{' '}
                Artifacts removed: {result.comparison.u2Primary.stats.artifactsRemoved},{' '}
                Dupes removed: {result.comparison.u2Primary.stats.duplicatesRemoved}
                {result.comparison.u2Primary.stats.detectedIntros && result.comparison.u2Primary.stats.detectedIntros.length > 0 && (
                  <div className="mt-1">
                    <span className="font-medium">Detected:</span>{' '}
                    {(result.comparison.u2Primary.stats.detectedIntros as Array<{name: string; role: string; speaker: string}>).map((i, idx) => (
                      <span key={idx} className="inline-block bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded mr-1 text-[10px]">
                        {i.name} → {i.role} (was: {i.speaker})
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Transcript Tabs */}
          <div className="bg-white border border-steel/20 rounded-lg overflow-hidden">
            <div className="flex border-b border-steel/10">
              <button
                onClick={() => setActiveTab('u2')}
                className={`px-4 py-2 text-sm font-medium ${activeTab === 'u2' ? 'bg-[#1F4FFF] text-white' : 'text-steel hover:text-navy'}`}
              >
                U2-Primary (new)
              </button>
              <button
                onClick={() => setActiveTab('current')}
                className={`px-4 py-2 text-sm font-medium ${activeTab === 'current' ? 'bg-[#1F4FFF] text-white' : 'text-steel hover:text-navy'}`}
              >
                P1-Primary (current)
              </button>
            </div>
            <div className="p-4 max-h-[600px] overflow-y-auto">
              <pre className="text-xs font-mono whitespace-pre-wrap leading-relaxed text-navy/80">
                {activeTab === 'u2' ? result.transcripts.u2Primary : result.transcripts.current}
              </pre>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
